import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { handleTrapKey } from '@/lib/focus-trap';

// [Brian 2026-10-30] "for inserting a link into a message body, i want to be able to add a link
// 'Title' and not just show the full link. Meaning, the hyperlink editor should have a field for a
// title and a link" — the old single URL prompt meant every link could only ever display its own
// address as its text.
//
// The house pattern for anything richer than one field is a dedicated modal (image-optimize-modal):
// createPortal, focus trapped, Escape closes, backdrop click closes. Enter in the title field moves
// to the URL field (pasting a link and hitting Enter there is the fast path); Enter on the URL saves.

export interface LinkEditResult {
  /** Normalization is the caller's job; this is exactly what the user typed. */
  href: string;
  /** Display text for the link. Empty means "show the address" (or keep the selected text). */
  title: string;
}

interface Props {
  /** Prefill for the URL field: the href of the link being edited, if any. */
  initialHref: string;
  /** Prefill for the title field: the link's current text, or the selection, if any. */
  initialTitle: string;
  onSave: (r: LinkEditResult) => void;
  onCancel: () => void;
}

export function LinkEditModal({ initialHref, initialTitle, onSave, onCancel }: Props) {
  const [href, setHref] = useState(initialHref);
  const [title, setTitle] = useState(initialTitle);
  const panel = useRef<HTMLDivElement>(null);
  const urlInput = useRef<HTMLInputElement>(null);

  const done = () => { if (href.trim()) onSave({ href, title }); };

  useEffect(() => {
    // 🚩 Return focus to whatever had it (the composer), the same contract ui-dialogs' Dialog keeps.
    const previous = document.activeElement as HTMLElement | null;
    panel.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { onCancel(); return; }
      handleTrapKey(e, panel.current);
    };
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('keydown', onKey); previous?.focus?.(); };
  }, [onCancel]);

  const field = 'mt-1 w-full rounded-lg border border-bmail-border bg-bmail-bg px-3 py-2 text-sm text-bmail-text focus:outline-none focus:ring-2 focus:ring-bmail-gold';

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4 backdrop-blur-[2px]"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      {/* 🚩 tabIndex={-1} so the panel can take focus for the trap (a plain div ignores .focus()). */}
      <div ref={panel} role="dialog" aria-modal="true" aria-label="Link"
        className="w-full max-w-md rounded-2xl border border-bmail-border bg-bmail-surface p-5 shadow-2xl focus:outline-none">
        <h3 className="mb-4 text-base font-semibold text-bmail-text">{initialHref ? 'Edit link' : 'Add a link'}</h3>
        <label className="text-xs text-bmail-text-secondary" htmlFor="link-title">Title</label>
        <input
          id="link-title" type="text" autoFocus value={title} placeholder="Text to show (optional)"
          onChange={(e) => setTitle(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') urlInput.current?.focus(); }}
          className={field}
        />
        <label className="mt-3 block text-xs text-bmail-text-secondary" htmlFor="link-url">URL</label>
        <input
          id="link-url" ref={urlInput} type="text" value={href} placeholder="https://…"
          onChange={(e) => setHref(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') done(); }}
          className={field}
        />
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onCancel}
            className="rounded-lg border border-bmail-border px-3 py-2 text-sm text-bmail-text hover:border-bmail-gold">
            Cancel
          </button>
          <button onClick={done} disabled={!href.trim()}
            className="rounded-lg bg-bmail-gold px-3 py-2 text-sm font-semibold text-black hover:opacity-90 disabled:opacity-40">
            Save
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}